// Marketing pixels (Meta, Google tag, TikTok) for the PUBLIC curated pages
// only. IDs come from /api/public/tracking (admin → Marketing & API). Every
// call is fail-safe: a pixel error must never break a buyer's page.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ATTR_KEY = "et_attribution";
const ATTR_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid", "gclid", "ttclid"];

let readyPromise = null;
let config = {};

function injectScript(src) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

function initMeta(id) {
  if (window.fbq) return;
  const n = (window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  });
  if (!window._fbq) window._fbq = n;
  n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
  injectScript("https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", id);
  window.fbq("track", "PageView");
}

function initGoogle(ids) {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
  }
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ids[0])}`);
  ids.forEach((id) => window.gtag("config", id));
}

function initTikTok(id) {
  if (window.ttq) return;
  window.TiktokAnalyticsObject = "ttq";
  const ttq = (window.ttq = []);
  ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group",
    "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
  ttq.setAndDefer = (t, e) => { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
  ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
  ttq.instance = (t) => { const e = ttq._i[t] || []; ttq.methods.forEach((m) => ttq.setAndDefer(e, m)); return e; };
  ttq.load = (e, n) => {
    const r = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r;
    ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
    ttq._o = ttq._o || {}; ttq._o[e] = n || {};
    injectScript(`${r}?sdkid=${e}&lib=ttq`);
  };
  ttq.load(id);
  ttq.page();
}

const googleIds = (cfg) => String(cfg.google_tag_id || "").split(/[\s,]+/).filter(Boolean);

export function loadTracking() {
  if (readyPromise) return readyPromise;
  readyPromise = fetch(`${API_BASE}/public/tracking`)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((cfg) => {
      config = cfg || {};
      try {
        if (config.meta_pixel_id) initMeta(config.meta_pixel_id);
        const gids = googleIds(config);
        if (gids.length) initGoogle(gids);
        if (config.tiktok_pixel_id) initTikTok(config.tiktok_pixel_id);
      } catch (e) { /* never break the page */ }
      return config;
    });
  return readyPromise;
}

const META = { view_content: "ViewContent", begin_checkout: "InitiateCheckout", add_payment_info: "AddPaymentInfo", purchase: "Purchase" };
const GOOGLE = { view_content: "view_item", begin_checkout: "begin_checkout", add_payment_info: "add_payment_info", purchase: "purchase" };
const TIKTOK = { view_content: "ViewContent", begin_checkout: "InitiateCheckout", add_payment_info: "AddPaymentInfo", purchase: "CompletePayment" };

// p: { content_id, content_name, value, currency, quantity, transaction_id, event_id }
export async function track(event, p = {}) {
  const cfg = await loadTracking();
  const value = Number(p.value || 0);
  const currency = (p.currency || "USD").toUpperCase();
  const eventId = p.event_id || `${event}-${p.content_id || "x"}-${Date.now()}`;
  try {
    if (window.fbq && META[event]) {
      window.fbq("track", META[event], {
        content_name: p.content_name, content_type: "product",
        content_ids: p.content_id ? [p.content_id] : undefined,
        value, currency, num_items: p.quantity || 1,
      }, { eventID: eventId });
    }
    if (window.gtag && GOOGLE[event]) {
      window.gtag("event", GOOGLE[event], {
        currency, value, transaction_id: p.transaction_id,
        items: [{ item_id: p.content_id, item_name: p.content_name, price: p.unit_price ?? value, quantity: p.quantity || 1 }],
      });
      if (event === "purchase" && cfg.google_ads_conversion_label) {
        const aw = googleIds(cfg).find((id) => id.startsWith("AW-"));
        if (aw) window.gtag("event", "conversion", { send_to: `${aw}/${cfg.google_ads_conversion_label}`, value, currency, transaction_id: p.transaction_id });
      }
    }
    if (window.ttq && TIKTOK[event]) {
      window.ttq.track(TIKTOK[event], {
        content_id: p.content_id, content_name: p.content_name, content_type: "product",
        value, currency, quantity: p.quantity || 1, event_id: eventId,
      });
    }
  } catch (e) { /* never break the page */ }
}

// Same event is sent once per browser session (e.g. Purchase on refresh).
export function trackOnce(key, event, params) {
  const k = `et_evt_${key}`;
  try {
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, "1");
  } catch (e) { /* storage blocked → just track */ }
  track(event, params);
}

export function getAttribution() {
  try { return JSON.parse(sessionStorage.getItem(ATTR_KEY) || "null"); } catch (e) { return null; }
}

// Stable anonymous id per browser so the agency can count unique visitors.
// Shared with the hub (curatedtrips.espiritutravel.com) so the funnel
// Hub → Landing → Itinerario joins on the same id: priority is the `vid`
// URL param the hub appends to the iframe, then the `et_vid` cookie on the
// root domain, then this origin's localStorage. Whatever wins is written
// back to the cookie (root domain) and localStorage.
const VID_COOKIE = "et_vid";
const ROOT_DOMAIN = "espiritutravel.com";

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (e) { return null; }
}

function persistVid(v) {
  try { localStorage.setItem("et_vid", v); } catch (e) { /* storage blocked */ }
  try {
    const host = window.location.hostname;
    if (host === ROOT_DOMAIN || host.endsWith("." + ROOT_DOMAIN)) {
      document.cookie = `${VID_COOKIE}=${encodeURIComponent(v)}; Domain=.${ROOT_DOMAIN}; Path=/; Max-Age=${60 * 60 * 24 * 400}; SameSite=None; Secure`;
    }
  } catch (e) { /* cookies blocked */ }
}

let _vidCache = null;
export function getVisitorId() {
  if (_vidCache) return _vidCache;
  try {
    const fromUrl = (new URLSearchParams(window.location.search).get("vid") || "").trim().slice(0, 64);
    const fromCookie = readCookie(VID_COOKIE);
    let v = fromUrl || fromCookie || localStorage.getItem("et_vid");
    if (!v) {
      v = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    }
    persistVid(v);
    _vidCache = v;
    return v;
  } catch (e) { return null; }
}

// First-party analytics beacon (view / reserve_open) — fire and forget.
// A "view" for the same trip is sent at most once every 2s, so React dev
// double-effects or quick re-mounts don't inflate the numbers.
const lastViewAt = {};
export function sendCuratedEvent(publicToken, type) {
  try {
    if (type === "view") {
      const now = Date.now();
      if (lastViewAt[publicToken] && now - lastViewAt[publicToken] < 2000) return;
      lastViewAt[publicToken] = now;
    }
    fetch(`${API_BASE}/public/curated/${publicToken}/events`, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type,
        visitor_id: getVisitorId(),
        referrer: document.referrer || null,
        landing_url: window.location.href.slice(0, 300),
        attribution: getAttribution(),
      }),
    }).catch(() => {});
  } catch (e) { /* never break the page */ }
}

// Remember utm_* / click ids from the ad link so the booking can be
// attributed to the campaign that brought the buyer.
export function captureAttribution() {
  try {
    const q = new URLSearchParams(window.location.search);
    const found = {};
    ATTR_PARAMS.forEach((k) => { const v = q.get(k); if (v) found[k] = v.slice(0, 300); });
    const existing = getAttribution();
    if (Object.keys(found).length === 0) {
      if (existing) return existing;
      const ref = document.referrer || "";
      if (!ref || ref.startsWith(window.location.origin)) return null;
      found.referrer = ref.slice(0, 300);
    } else if (document.referrer) {
      found.referrer = document.referrer.slice(0, 300);
    }
    found.landing_url = window.location.href.slice(0, 300);
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(found));
    return found;
  } catch (e) { return null; }
}
